import {
  FlightCustomDataI,
  InsuranceCustomDataI,
  StateroomDetailsProps,
} from '@msc-b2c/interfaces/types';
import styles from './stateroomdetails.module.scss';
import StateroomDetails from './StateroomDetails';
import { getActiveSession, GetLabelsMyMSC, useGetBookingQuery } from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import { BookingStatusEnum, LabelItemsTypeEnum } from '@msc-b2c/interfaces/enums';
import { getInsuranceEnrichCustomDataInfo } from '../utils/insuranceUtils';
import { getFlightEnrichedCustomDataInfo } from '../utils/flightsUtils';
import { getTransferEnrichedCustomDataInfo } from '../utils/transfersUtils';
import { getCabinIdOfTheLoggedUser } from '../utils/myAreaUtils';

export function StateroomDetailsWrapper({
  stateroomCardTitle,
  passengerSectionTitle,
  passengerLabel,
  roomDetailsTitle,
  roomDetailsElements,
  includedSectionTitle,
  guaranteedCabinLabel,
  viewDetailsLabel,
  alwaysIncludedTitle,
  alwaysIncludedElements,
  diningSectionTitle,
  diningLabel,
  passengerDescriptionEuropeInsurancePlaceholder,
  passengerDescriptionNoEuropeInsurancePlaceholder,
  flightsSectionIcon,
  flightsSectionTitle,
  flightsStatusLabel,
  airlineLabel,
  flightClassLabel,
  flightDetailsLabel,
  flightDetailsPlaceholder,
  transfersSectionIcon,
  transfersSectionTitle,
  transferDescriptionPlaceholder,
  openViewDetailsIncludedIcon,
  closeViewDetailsIncludedIcon,
  hotelSectionIcon,
  hotelSectionTitle,
  hotelLabels,
  excursionSectionTitle,
  excursionIcon,
  excursionIncludedLabel,
  miscellaneousSectionTitle,
  miscellaneousIcon,
  miscellaneousIncludedLabel,
}: Readonly<StateroomDetailsProps>) {
  const props = {
    stateroomCardTitle,
    passengerSectionTitle,
    passengerLabel,
    roomDetailsTitle,
    hotelSectionIcon,
    hotelSectionTitle,
    hotelLabels,
    guaranteedCabinLabel,
    roomDetailsElements,
    includedSectionTitle,
    viewDetailsLabel,
    alwaysIncludedTitle,
    alwaysIncludedElements,
    diningSectionTitle,
    diningLabel,
    passengerDescriptionEuropeInsurancePlaceholder,
    passengerDescriptionNoEuropeInsurancePlaceholder,
    openViewDetailsIncludedIcon,
    closeViewDetailsIncludedIcon,
    flightsSectionIcon,
    flightsSectionTitle,
    flightsStatusLabel,
    airlineLabel,
    flightClassLabel,
    flightDetailsLabel,
    flightDetailsPlaceholder,
    transfersSectionIcon,
    transfersSectionTitle,
    transferDescriptionPlaceholder,
    excursionSectionTitle,
    excursionIcon,
    excursionIncludedLabel,
    miscellaneousSectionTitle,
    miscellaneousIcon,
    miscellaneousIncludedLabel,
  };
  const [labelsMap, setLabelsMap] = useState<
    Record<string, { description?: string; specialInstructions?: string }>
  >({});
  const { bookingNumber = '', cruiseId } = getActiveSession();

  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const cabinsBooked = bookingData?.cabins || [];

  /**
   * Filters and sorts the list of booked cabins for the active cruise.
   * Sorting logic:
   * - The logged user's cabin is prioritized and placed at the top of the list.
   * - Other cabins are sorted in their original order.
   */
  const activeCruiseCabinsBooked = cabinsBooked
    .filter((cabin) => cabin.cruise.id === cruiseId)
    .sort((a, b) => {
      const loggedUserCabinId = getCabinIdOfTheLoggedUser(bookingData);
      if (a.id === loggedUserCabinId) {
        return -1;
      }
      if (b.id === loggedUserCabinId) {
        return 1;
      }
      return 0;
    });

  const onBoardServicesData = useMemo(() => bookingData?.onboardServices || [], [bookingData]);

  const retrieveLabels = (codes: string[], type: string) => {
    const uniqueCodes = Array.from(new Set(codes)).join(',');

    const getLabels = new GetLabelsMyMSC(false, { type: type, codes: uniqueCodes });
    getLabels
      .getData()
      .then((res) => {
        const newLabelsMap: Record<string, { description?: string; specialInstructions?: string }> =
          {};

        if (res?.data) {
          res.data.forEach((labelsActivityData) => {
            newLabelsMap[labelsActivityData.code] = {
              description: labelsActivityData.description || '',
              specialInstructions: labelsActivityData.longDescription || '',
            };
          });
        }

        setLabelsMap((prev) => ({ ...prev, ...newLabelsMap }));
      })
      .catch((error) => {
        console.error('Error fetching onboard labels:', error);
      });
  };

  useEffect(() => {
    if (bookingData) {
      const insuranceListData = bookingData.insuranceItems || [];
      const flightData = bookingData.airItems || [];
      const transferData = bookingData.transfers || [];

      if (flightData && flightData.length > 0) {
        retrieveLabels(
          flightData
            .flatMap((flight) => flight.id)
            .filter((id): id is string => typeof id === 'string'),
          LabelItemsTypeEnum.FLY
        );
      }
      if (transferData && transferData.length > 0) {
        retrieveLabels(
          transferData.flatMap((transfer) => transfer.code),
          LabelItemsTypeEnum.TRANSFER
        );
      }
      if (insuranceListData && insuranceListData.length > 0) {
        retrieveLabels(
          insuranceListData.flatMap((insurance) => insurance.code),
          LabelItemsTypeEnum.INSURANCE
        );
      }
      if (onBoardServicesData && onBoardServicesData.length > 0) {
        retrieveLabels(
          onBoardServicesData.flatMap((obs) => obs.code),
          LabelItemsTypeEnum.OBS
        );
      }
      const hotelListData = bookingData.hotels || [];
      if (hotelListData.length > 0) {
        retrieveLabels(
          hotelListData.flatMap((hotel) => hotel.code),
          LabelItemsTypeEnum.HOTEL
        );
      }
    }
  }, [bookingData]);

  return (
    <div className={styles.stateroomDetailsWrapper}>
      {bookingData &&
        activeCruiseCabinsBooked?.map((cabin, index) => {
          const insuranceListByCabinData: InsuranceCustomDataI[] = getInsuranceEnrichCustomDataInfo(
            cabin.id,
            bookingData,
            {
              europeTextPlaceholder: passengerDescriptionEuropeInsurancePlaceholder,
              noEuropeTextPlaceholder: passengerDescriptionNoEuropeInsurancePlaceholder,
              passengerLabelPlaceholder: passengerLabel,
            },
            labelsMap
          );

          // Filter flights data for the current cabin
          const flightsData: FlightCustomDataI[] = getFlightEnrichedCustomDataInfo(
            cabin.id,
            bookingData,
            {
              flightsSectionIcon,
              flightsSectionTitle,
              statusLabel: flightsStatusLabel,
              airlineLabel,
              classLabel: flightClassLabel,
              detailsLabel: flightDetailsLabel,
            },
            {
              passengerLabelPlaceholder: passengerLabel,
              flightDetailsPlaceholder,
            },
            labelsMap
          );

          // Filter transfers data for the current cabin
          const transfersData = getTransferEnrichedCustomDataInfo(
            cabin.id,
            bookingData,
            {
              transferDescriptionPlaceholder,
            },
            labelsMap
          );

          //Filter passengers for the current cabin
          const passengerData =
            bookingData?.passengers?.filter((passenger) =>
              cabin.passengers?.some((cabinPassenger) => cabinPassenger.id === passenger.id)
            ) || [];

          // Filter onBoardServicesData for passengers in the current cabin
          const passengerIdsInCabin = new Set(passengerData.map((p) => p.id));
          const cabinOnBoardServices = onBoardServicesData.filter(
            (service) =>
              service.bookingStatus?.code === BookingStatusEnum.BOOKED &&
              service.relatedItem?.id === cabin.id &&
              service.passengers?.some(
                (passenger) => passenger.id && passengerIdsInCabin.has(passenger.id)
              )
          );

          const hotelsForCabin =
            bookingData?.hotels?.filter((hotel) => hotel.relatedItem?.id === cabin.id) || [];

          const excursionsForCabin =
            bookingData?.excursions?.filter(
              (excursion) =>
                excursion.relatedItem?.id === cabin.id &&
                excursion.passengers?.some((passenger) => passengerIdsInCabin.has(passenger.id!))
            ) || [];

          const miscellaneousItemsForCabin =
            bookingData?.miscellaneousItems?.filter(
              (item) =>
                item.relatedItem?.id === cabin.id &&
                item.passengers?.some((passenger) => passengerIdsInCabin.has(passenger.id!))
            ) || [];

          // Dining Options Info
          const diningOptionsData = cabin.diningDetails;

          //Filter cabin data for the current cabin

          const { number, categoryCode, categoryDescription } = cabin;

          const deckDescription = cabin.deck?.description || '';
          const cabinData = {
            number,
            deckDescription,
            categoryCode,
            categoryDescription,
          };

          return (
            <StateroomDetails
              {...props}
              key={cabin.id}
              stateroomIndex={index}
              stateroomCount={activeCruiseCabinsBooked.length}
              passengersData={passengerData}
              cabinData={cabinData}
              onBoardServicesData={cabinOnBoardServices}
              diningOptionsData={diningOptionsData}
              insuranceData={insuranceListByCabinData}
              labelsMap={labelsMap}
              hotelData={hotelsForCabin}
              hotelSectionIcon={hotelSectionIcon}
              hotelSectionTitle={hotelSectionTitle}
              hotelLabels={hotelLabels}
              excursionData={excursionsForCabin}
              miscellaneousItems={miscellaneousItemsForCabin}
              flightsData={flightsData}
              transfersData={transfersData}
            />
          );
        })}
    </div>
  );
}

StateroomDetailsWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/stateroomdetails';

export default StateroomDetailsWrapper;
